import $ from "jquery";

const HOLD_MS = 450;

/**
 * “Tap to toggle / hold to show” tooltip layer (avoids long-delay native `title` and works on touch).
 *
 * @param {string} namespace Event namespace fragment (e.g. `home` or `overview`)
 * @param {JQuery} $root Root element for delegated events (e.g. `.app` container)
 */
export function createInfoTooltipLayer(namespace, $root) {
    const ns = `bdit_${namespace}`;
    const winNs = `${ns}W`;
    const triggerClass = "bd-info-tooltip-trigger";
    const sel = `[data-info-tooltip]`;

    /** @type {JQuery | null} */
    let $bubble = null;
    /** @type {HTMLElement | null} */
    let activeEl = null;
    let holdTimer = null;
    let holdSuppressClick = false;

    const $doc = $(document);
    const $win = $(window);
    const ptrDismissNs = `${ns}PtrD`;
    let onReposition;
    let onKey;
    let onOut;
    let ptrDismissTimer = null;

    const removeBubble = () => {
        if (onReposition) {
            $win.off(`.${winNs}`);
            onReposition = null;
        }
        if (onKey) {
            $doc.off(`.${ns}esc`, onKey);
            onKey = null;
        }
        if (onOut) {
            $doc.off(`.${ns}out`, onOut);
            onOut = null;
        }
        $doc.off(`.${ptrDismissNs}`);
        if (ptrDismissTimer != null) {
            clearTimeout(/** @type {ReturnType<typeof setTimeout>} */(ptrDismissTimer));
            ptrDismissTimer = null;
        }
        if ($bubble && $bubble.length) {
            $bubble.remove();
        }
        $bubble = null;
        activeEl = null;
    };

    const getText = (el) => {
        if (!el || !(el instanceof Element)) {
            return "";
        }
        return (el.getAttribute("data-info-tooltip") || "").trim();
    };

    const position = (el) => {
        if (!el || !$bubble?.length) {
            return;
        }
        if (!el.isConnected) {
            return removeBubble();
        }
        const r = el.getBoundingClientRect();
        const margin = 8;
        $bubble.css({ position: "fixed", visibility: "hidden", top: 0, left: 0 });
        const w = $bubble.outerWidth() || 280;
        const h = $bubble.outerHeight() || 1;
        let top = r.bottom + margin;
        if (top + h > window.innerHeight - 8) {
            top = Math.max(8, r.top - h - margin);
        }
        let left = r.left + r.width / 2 - w / 2;
        left = Math.max(8, Math.min(left, window.innerWidth - w - 8));
        $bubble.css({ top, left, visibility: "visible" });
    };

    /**
     * @param {HTMLElement} el
     * @param {{ fromHold?: boolean }} [opts] fromHold is legacy (same dismiss behaviour for all)
     */
    const show = (el, opts) => {
        const text = getText(el);
        if (!text) {
            return;
        }
        if (onOut) {
            $doc.off(`.${ns}out`, onOut);
            onOut = null;
        }
        $doc.off(`.${ptrDismissNs}`);
        if (ptrDismissTimer != null) {
            clearTimeout(/** @type {ReturnType<typeof setTimeout>} */(ptrDismissTimer));
            ptrDismissTimer = null;
        }

        if (activeEl === el && $bubble && $bubble.length) {
            removeBubble();
            return;
        }
        removeBubble();
        activeEl = el;
        $bubble = $('<div class="bd-info-tooltip" role="status" aria-live="polite"></div>').text(
            text,
        );
        $("body").append($bubble);
        onKey = (e) => {
            if (e.key === "Escape" || e.key === "Esc") {
                e.preventDefault();
                removeBubble();
            }
        };
        $doc.on(`keydown.${ns}esc`, onKey);
        onReposition = () => {
            if (activeEl && $bubble && $bubble.length) {
                position(activeEl);
            }
        };
        $win.on(`resize.${winNs} scroll.${winNs}`, onReposition);
        position(el);
        requestAnimationFrame(() => {
            if (activeEl === el) {
                position(el);
            }
        });
        setTimeout(() => {
            onOut = (e) => {
                if (!$bubble || !activeEl) {
                    return;
                }
                const t = e.target;
                if (t == null) {
                    return;
                }
                if ($bubble[0] && ($bubble[0] === t || $bubble[0].contains(/** @type {Node} */(t)))) {
                    return;
                }
                if (activeEl === t || activeEl.contains(/** @type {Node} */(t))) {
                    return;
                }
                removeBubble();
            };
            $doc.on(`click.${ns}out touchstart.${ns}out`, onOut);
        }, 0);

        // After open (click or long-press): move pointer off the (i) or press elsewhere — no second click on the (i) needed.
        const delayMs = opts && opts.fromHold ? 120 : 40;
        ptrDismissTimer = setTimeout(() => {
            ptrDismissTimer = null;
            if (!$bubble || !activeEl) {
                return;
            }
            const dismissOnInteraction = (e) => {
                if (!$bubble || !activeEl) {
                    $doc.off(`.${ptrDismissNs}`);
                    return;
                }
                if (e.type === "mousemove" || e.type === "touchmove") {
                    const t = e.target;
                    if (
                        t != null &&
                        (activeEl === t || activeEl.contains(/** @type {Node} */(t)))
                    ) {
                        return;
                    }
                    removeBubble();
                    $doc.off(`.${ptrDismissNs}`);
                    return;
                }
                const t2 = e.target;
                if (t2 == null) {
                    return;
                }
                if ($bubble[0] && $bubble[0].contains(/** @type {Node} */(t2))) {
                    return;
                }
                if (activeEl === t2 || activeEl.contains(/** @type {Node} */(t2))) {
                    return;
                }
                removeBubble();
                $doc.off(`.${ptrDismissNs}`);
            };
            $doc.on(
                `mousemove.${ptrDismissNs} touchmove.${ptrDismissNs}`,
                dismissOnInteraction,
            );
            $doc.on(
                `click.${ptrDismissNs} mousedown.${ptrDismissNs} touchstart.${ptrDismissNs}`,
                dismissOnInteraction,
            );
        }, delayMs);
    };

    const startHold = (el) => {
        if (holdTimer != null) {
            clearTimeout(/** @type {ReturnType<typeof setTimeout>} */(holdTimer));
            holdTimer = null;
        }
        if (!(el instanceof HTMLElement) || !getText(el)) {
            return;
        }
        holdTimer = setTimeout(() => {
            holdTimer = null;
            holdSuppressClick = true;
            show(el, { fromHold: true });
        }, HOLD_MS);
    };

    const endHold = () => {
        if (holdTimer != null) {
            clearTimeout(/** @type {ReturnType<typeof setTimeout>} */(holdTimer));
            holdTimer = null;
        }
    };

    const onPointerDown = (e) => {
        if (e.type === "mousedown" && e.button !== 0) {
            return;
        }
        const t = e.currentTarget;
        if (t instanceof HTMLElement) {
            startHold(t);
        }
    };

    const onClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const t = e.currentTarget;
        if (!(t instanceof HTMLElement)) {
            return;
        }
        if (holdSuppressClick) {
            holdSuppressClick = false;
            return;
        }
        if (!getText(t)) {
            return;
        }
        show(t, { fromHold: false });
    };

    $root
        .on(`click.${ns}`, sel, onClick)
        .on(
            `touchstart.${ns} mousedown.${ns}`,
            sel,
            onPointerDown,
        )
        .on(
            `touchend.${ns} touchcancel.${ns} mouseup.${ns} mouseleave.${ns}`,
            sel,
            (e) => {
                e.stopPropagation();
                endHold();
            },
        );

    /**
     * Set `data-info-tooltip` and optional `aria-label` (if missing) on a trigger; marks as interactive.
     * @param {JQuery} $btn
     * @param {string} text
     */
    const setTriggerText = ($btn, text) => {
        const t = (text && String(text).trim()) || "";
        if (!$btn.length) {
            return;
        }
        $btn.addClass(triggerClass);
        $btn.removeAttr("title");
        if (t) {
            $btn.attr("data-info-tooltip", t);
            $btn.attr("aria-label", t);
        } else {
            $btn.removeAttr("data-info-tooltip");
            $btn.removeAttr("aria-label");
        }
    };

    return {
        setTriggerText,
        close: removeBubble,
        destroy: () => {
            $root.off(`.${ns}`);
            removeBubble();
        },
    };
}
